/**
 * Install Prompt Scene - Enhanced
 *
 * Features:
 * - Browser-specific installation messaging
 * - Desktop QR code for mobile redirect
 * - Installation state polling for manual installs
 * - Post-installation success screen with "Open in App" guidance
 * - Clean dark background matching main menu
 * - Animated MenuBlobo character
 * - GlassButton components
 * - Proper spacing and safe area handling
 */

import Phaser from 'phaser';
import { pwaManager } from '@/main';
import { MenuBlobo } from '../ui/components/MenuBlobo';
import { GlassButton } from '../ui/components/GlassButton';
import { MenuParticleSystem } from '../ui/effects/MenuParticleSystem';
import { UI_COLORS, TYPOGRAPHY, Z_INDEX } from '../config/designSystem';
import { ICONS } from '../config/icons';

export class InstallPromptScene extends Phaser.Scene {
  private isShowingSuccessScreen: boolean = false;
  private blobo?: MenuBlobo;
  private qrCodeImage?: Phaser.GameObjects.Image;

  constructor() {
    super({ key: 'InstallPromptScene' });
  }

  create(): void {
    // Note: This scene is launched as an overlay from MainMenuScene
    // It displays browser-specific installation instructions

    // Prevent scene from closing when page visibility changes
    this.setupVisibilityHandler();

    // Set up install listener
    this.setupInstallListener();

    // Start polling for manual installations
    this.startInstallationPolling();

    // Opaque overlay background to prevent display issues with main menu
    const overlay = this.add.rectangle(
      0,
      0,
      this.cameras.main.width,
      this.cameras.main.height,
      0x0a0e27, // Same dark background as main menu
      1.0 // Fully opaque
    );
    overlay.setOrigin(0, 0);
    overlay.setDepth(Z_INDEX.modal);
    overlay.setScrollFactor(0);

    // Add floating particles (managed by Phaser scene graph)
    new MenuParticleSystem(this);

    // Wait a frame then show content
    this.time.delayedCall(100, () => {
      // Check if app is currently running as PWA or was installed before
      if (!pwaManager.canInstall() || pwaManager.wasInstalledBefore()) {
        this.showSuccessScreen();
        return;
      }

      this.showInstallPrompt();
    });
  }

  /**
   * Show the install prompt UI with browser-specific messaging
   */
  private showInstallPrompt(): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const height = this.cameras.main.height;
    const browserInfo = pwaManager.getBrowserInfo();

    // Animated Blobo character
    const bloboY = Math.min(centerY - 140, height * 0.25);
    this.blobo = new MenuBlobo(this, centerX, bloboY, 70);
    this.blobo.setDepth(Z_INDEX.tooltip);

    // Title with browser-specific messaging
    let titleText = 'Install BLOBO';
    if (browserInfo.isDesktop) {
      titleText = 'Blobo';
    } else if (browserInfo.name === 'firefox') {
      titleText = 'Best in Chrome!';
    } else if (browserInfo.isIOS && browserInfo.name !== 'safari') {
      titleText = 'Best in Safari!';
    }

    const title = this.add.text(centerX, bloboY + 110, titleText, {
      fontFamily: TYPOGRAPHY.fonts.display,
      fontSize: `${TYPOGRAPHY.sizes.title}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    title.setOrigin(0.5);
    title.setDepth(Z_INDEX.tooltip);

    // Subtitle - browser specific
    let subtitleText = 'Get the full experience';
    if (browserInfo.isDesktop) {
      subtitleText =
        'This game is based on motion controls and works best on your iOS or Android device.';
    } else if (browserInfo.name === 'firefox') {
      subtitleText = 'For full-screen gameplay';
    }

    const subtitle = this.add.text(centerX, bloboY + 155, subtitleText, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: UI_COLORS.primaryDark,
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    subtitle.setOrigin(0.5);
    subtitle.setDepth(Z_INDEX.tooltip);

    // Desktop: Show QR code and video
    let instructionCount = 0;
    if (browserInfo.isDesktop) {
      // Position video/QR starting after subtitle with some spacing
      this.showQRCode(centerX, bloboY + 190);
    } else {
      // Mobile: Show benefits list and get instruction count for layout
      instructionCount = this.showBenefitsList(centerX, bloboY + 200, browserInfo);
    }

    // Install button - only show when it has a meaningful action
    // Don't show for iOS Safari, Samsung, or generic browsers that only have manual instructions
    const shouldShowButton =
      browserInfo.isDesktop ||
      browserInfo.name === 'chrome' ||
      browserInfo.name === 'edge' ||
      browserInfo.name === 'firefox' ||
      (browserInfo.isIOS && browserInfo.name !== 'safari');

    if (shouldShowButton) {
      // Install button text and icon based on browser
      let buttonText = 'INSTALL APP';
      let buttonIcon: string | undefined = ICONS.INSTALL_MOBILE;

      if (browserInfo.isDesktop) {
        buttonText = 'or play on desktop';
        buttonIcon = undefined;
      } else if (browserInfo.name === 'firefox') {
        buttonText = 'OPEN IN CHROME';
        buttonIcon = ICONS.EXPLORE;
      } else if (browserInfo.isIOS && browserInfo.name !== 'safari') {
        buttonText = 'OPEN IN SAFARI';
        buttonIcon = ICONS.EXPLORE;
      }

      // Install button
      // Calculate button position based on instruction count to avoid overlay
      const instructionHeight = instructionCount * 32; // 32px per instruction line (18px font + 14px spacing)
      let buttonY: number;

      if (browserInfo.isDesktop) {
        // Desktop: text-only button at bottom
        buttonY = height - 80;
        const desktopButton = this.add.text(centerX, buttonY, buttonText, {
          fontFamily: TYPOGRAPHY.fonts.josefin,
          fontSize: `${TYPOGRAPHY.sizes.body}px`,
          color: '#9CA3AF',
          resolution: Math.max(4, window.devicePixelRatio * 2),
        });
        desktopButton.setOrigin(0.5);
        desktopButton.setDepth(Z_INDEX.tooltip);
        desktopButton.setInteractive({ useHandCursor: true });

        desktopButton.on('pointerover', () => {
          desktopButton.setColor('#FFFFFF');
        });

        desktopButton.on('pointerout', () => {
          desktopButton.setColor('#9CA3AF');
        });

        desktopButton.on('pointerup', () => {
          this.closeScene();
        });
      } else {
        // Mobile: prominent button with icon
        buttonY = bloboY + 200 + instructionHeight + 40; // 40px spacing after instructions
        const installButton = new GlassButton(this, centerX, buttonY, {
          text: buttonText,
          iconKey: buttonIcon,
          fontSize: 22,
          width: 260,
          height: 64,
          style: 'primary',
          onClick: () => this.handleInstall(),
        });
        installButton.setDepth(Z_INDEX.tooltip);

        // Add pulse animation
        this.tweens.add({
          targets: installButton,
          scale: { from: 1.0, to: 1.05 },
          duration: 1500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }

    // Close button (X)
    const closeButton = this.add.text(this.cameras.main.width - 30, 30, '✕', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '28px',
      color: '#9CA3AF',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    closeButton.setOrigin(0.5);
    closeButton.setDepth(Z_INDEX.notification);
    closeButton.setInteractive({ useHandCursor: true });

    closeButton.on('pointerover', () => {
      closeButton.setColor('#FFFFFF');
      closeButton.setScale(1.2);
    });

    closeButton.on('pointerout', () => {
      closeButton.setColor('#9CA3AF');
      closeButton.setScale(1.0);
    });

    closeButton.on('pointerup', () => {
      this.closeScene();
    });

    // Continue in browser link (hide for desktop)
    if (!browserInfo.isDesktop) {
      const continueText = this.add.text(centerX, height - 50, 'Continue in browser', {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.caption}px`,
        color: '#6B7280',
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      continueText.setOrigin(0.5);
      continueText.setDepth(Z_INDEX.tooltip);
      continueText.setInteractive({ useHandCursor: true });

      continueText.on('pointerover', () => {
        continueText.setColor('#9CA3AF');
      });

      continueText.on('pointerout', () => {
        continueText.setColor('#6B7280');
      });

      continueText.on('pointerup', () => {
        this.closeScene();
      });
    }

    // Pulse title
    this.tweens.add({
      targets: title,
      scale: { from: 1.0, to: 1.02 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Show installation instructions for mobile users
   * Returns the number of instruction lines displayed
   */
  private showBenefitsList(centerX: number, startY: number, browserInfo: any): number {
    // Browser-specific instructions
    let instructions: string[] = [];

    if (browserInfo.name === 'safari' && browserInfo.isIOS) {
      instructions = [
        '1. Tap Menu button (⋯)',
        '2. Tap "Share"',
        '3. Tap "More"',
        '4. Tap "Add to Home Screen"',
        '5. Tap "Add" to confirm',
      ];
    } else if (browserInfo.isIOS) {
      instructions = [
        '1. Open Safari browser',
        '2. Go to blobo.app',
        '3. Tap Menu (⋯) → Share → More',
        '4. Tap "Add to Home Screen"',
      ];
    } else if (browserInfo.name === 'chrome' || browserInfo.name === 'edge') {
      // Chrome/Edge manual installation instructions
      instructions = [
        '1. Tap menu button (⋮)',
        '2. Tap "Add to Home screen"',
        '3. Tap "Install" or "Add"',
      ];
    } else if (browserInfo.name === 'firefox') {
      instructions = [
        '1. Tap button below to open Chrome',
        '2. Or use Firefox menu (⋮)',
        '3. Tap "Add to Home screen"',
        '⚠ Firefox has limited full-screen',
      ];
    } else if (browserInfo.name === 'samsung') {
      instructions = [
        '1. Tap menu button (⋮)',
        '2. Tap "Add page to"',
        '3. Tap "Home screen"',
        '4. Tap "Add" to confirm',
      ];
    } else {
      // Generic Android
      instructions = [
        '1. Tap menu button (⋮)',
        '2. Look for "Install app"',
        '3. Or "Add to Home screen"',
        '4. Tap to install',
      ];
    }

    let instructionY = startY;
    instructions.forEach(instruction => {
      const text = this.add.text(centerX, instructionY, instruction, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.body}px`,
        color: instruction.includes('⚠') ? '#FBBF24' : '#FFFFFF',
        align: 'center',
        resolution: Math.max(4, window.devicePixelRatio * 2),
      });
      text.setOrigin(0.5);
      text.setDepth(Z_INDEX.tooltip);
      instructionY += 32; // Increased spacing for larger font
    });

    return instructions.length;
  }

  /**
   * Show QR code for desktop users with YouTube video (two-column layout)
   */
  private async showQRCode(centerX: number, startY: number): Promise<void> {
    const height = this.cameras.main.height;

    // Calculate responsive sizing based on available height
    const availableHeight = height - startY - 100; // Reserve 100px for bottom button
    const videoWidth = Math.min(250, availableHeight * 0.5625); // Max 250px width, maintain 9:16 aspect
    const videoHeight = videoWidth * 1.778; // 9:16 aspect ratio
    const qrSize = Math.min(200, availableHeight * 0.35);

    // Two-column layout: center the entire layout as a whole
    const gap = 30; // Gap between video and QR content
    const totalWidth = videoWidth + gap + qrSize; // Total width of both columns
    const layoutStartX = centerX - totalWidth / 2; // Start position to center entire layout

    const videoX = layoutStartX; // Video at start of layout
    const rightContentX = layoutStartX + videoWidth + gap; // Right content after video + gap

    // LEFT COLUMN: YouTube video
    const videoY = startY;

    // Get canvas position in browser window to properly position absolute iframe
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();

    // Calculate absolute position: canvas offset + canvas-relative position
    const absoluteVideoX = canvasRect.left + videoX;
    const absoluteVideoY = canvasRect.top + videoY;

    // Create iframe element for YouTube embed
    const iframe = document.createElement('iframe');
    iframe.width = `${videoWidth}`;
    iframe.height = `${videoHeight}`;
    iframe.src =
      'https://www.youtube.com/embed/dVbVl5X1a8A?autoplay=1&mute=1&controls=0&loop=1&playlist=dVbVl5X1a8A&playsinline=1';
    iframe.title = 'Blobo Gameplay';
    iframe.style.position = 'absolute';
    iframe.style.left = `${absoluteVideoX}px`;
    iframe.style.top = `${absoluteVideoY}px`;
    iframe.style.border = 'none';
    iframe.style.borderRadius = '12px';
    iframe.style.zIndex = '100';
    iframe.allow = 'autoplay; encrypted-media';

    // Add to DOM
    document.body.appendChild(iframe);

    // Store reference for cleanup
    this.events.on('shutdown', () => {
      iframe.remove();
    });

    // RIGHT COLUMN: QR code and instructions (left-aligned, starting at same Y as video)
    const qrX = rightContentX + qrSize / 2; // Center of QR code
    const qrY = startY + qrSize / 2; // Start at same Y as video

    // QR code container background
    const qrBg = this.add.rectangle(qrX, qrY, qrSize + 20, qrSize + 20, 0xffffff, 1);
    qrBg.setDepth(Z_INDEX.tooltip);

    // Loading text
    const loadingText = this.add.text(qrX, qrY, 'Loading QR...', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#000000',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    loadingText.setOrigin(0.5);
    loadingText.setDepth(Z_INDEX.notification);

    try {
      // Generate QR code
      const qrUrl = await pwaManager.generateQRCode();

      // Load QR code as texture
      this.load.image('qr-code', qrUrl);
      this.load.once('complete', () => {
        loadingText.destroy();

        this.qrCodeImage = this.add.image(qrX, qrY, 'qr-code');
        this.qrCodeImage.setDisplaySize(qrSize, qrSize);
        this.qrCodeImage.setDepth(Z_INDEX.notification);
      });
      this.load.start();
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      loadingText.setText('blobo.app');
      loadingText.setColor('#000000');
    }

    // Instructions below QR code (left-aligned with QR)
    const instructionsY = startY + qrSize + 20;
    const instructions = this.add.text(
      rightContentX,
      instructionsY,
      'Scan to play on mobile\nwith motion controls!',
      {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.caption}px`,
        color: '#FFFFFF',
        align: 'left',
        lineSpacing: 4,
        resolution: Math.max(4, window.devicePixelRatio * 2),
      }
    );
    instructions.setOrigin(0, 0); // Top-left origin for left-aligned text
    instructions.setDepth(Z_INDEX.tooltip);
  }

  /**
   * Setup visibility change handler to prevent auto-close on background
   */
  private setupVisibilityHandler(): void {
    // Prevent scene from being affected when page visibility changes
    // This keeps the post-install dialog visible when user checks their app drawer

    // Handle game pause (happens when page is backgrounded)
    this.game.events.on('pause', this.handleGamePause, this);
    this.game.events.on('resume', this.handleGameResume, this);

    // Clean up on scene shutdown
    this.events.once('shutdown', () => {
      this.game.events.off('pause', this.handleGamePause, this);
      this.game.events.off('resume', this.handleGameResume, this);
    });
  }

  /**
   * Handle game pause - keep scene visible
   */
  private handleGamePause(): void {
    // Don't stop the scene when game pauses
    // This allows the post-install UI to stay visible when user backgrounds the app
  }

  /**
   * Handle game resume - ensure scene is still visible
   */
  private handleGameResume(): void {
    // Ensure scene stays active after resume
    // This prevents the dialog from closing when user returns from app drawer
  }

  /**
   * Setup listener for successful app installation
   */
  private setupInstallListener(): void {
    // Listen for appinstalled event
    pwaManager.onInstalled(() => {
      if (!this.isShowingSuccessScreen) {
        this.showSuccessScreen();
      }
    });
  }

  /**
   * Start polling for manual installations (e.g., via browser menu)
   */
  private startInstallationPolling(): void {
    pwaManager.startInstallationPolling(installed => {
      if (installed && !this.isShowingSuccessScreen) {
        this.showSuccessScreen();
      }
    });
  }

  /**
   * Show success screen after installation
   */
  private showSuccessScreen(): void {
    this.isShowingSuccessScreen = true;

    // Clear the scene
    this.children.removeAll();
    this.tweens.killAll();

    // Recreate opaque background overlay
    const overlay = this.add.rectangle(
      0,
      0,
      this.cameras.main.width,
      this.cameras.main.height,
      0x0a0e27,
      1.0
    );
    overlay.setOrigin(0, 0);
    overlay.setDepth(Z_INDEX.modal);
    overlay.setScrollFactor(0);

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const browserInfo = pwaManager.getBrowserInfo();

    // Success checkmark - positioned higher
    const checkmark = this.add.text(centerX, centerY - 180, '✅', {
      fontSize: '64px',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    checkmark.setOrigin(0.5);
    checkmark.setDepth(Z_INDEX.tooltip);

    // Success title
    const title = this.add.text(centerX, centerY - 100, 'App Installed!', {
      fontFamily: TYPOGRAPHY.fonts.display,
      fontSize: `${TYPOGRAPHY.sizes.heading}px`,
      color: '#10B981',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    title.setOrigin(0.5);
    title.setDepth(Z_INDEX.tooltip);

    // Instructions - Browser specific guidance
    let instructionText = '';
    if (browserInfo.name === 'chrome' || browserInfo.name === 'edge') {
      instructionText =
        'To launch BLOBO:\n\n' +
        '1. Open your app list/drawer\n' +
        '2. Find "Blobo" app\n' +
        '3. Tap to launch in full-screen\n\n' +
        '💡 Quick access:\n' +
        'Browser menu (⋮) → "Open in Blobo"';
    } else if (browserInfo.isIOS) {
      instructionText =
        'To launch BLOBO:\n\n' +
        '1. Go to your home screen\n' +
        '2. Find the BLOBO icon\n' +
        '3. Tap to launch the app';
    } else {
      instructionText =
        'To launch BLOBO:\n\n' +
        '1. Open your app list/drawer\n' +
        '2. Find "Blobo" app\n' +
        '3. Tap to launch';
    }

    const instructions = this.add.text(centerX, centerY - 30, instructionText, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#FFFFFF',
      align: 'center',
      lineSpacing: 6,
      resolution: Math.max(4, window.devicePixelRatio * 2),
    });
    instructions.setOrigin(0.5, 0); // Anchor at top-center instead of center to prevent upward overlap
    instructions.setDepth(Z_INDEX.tooltip);

    // Close button - positioned at top-right
    const width = this.cameras.main.width;
    const closeButton = new GlassButton(this, width - 70, 60, {
      text: '✕',
      fontSize: 28,
      width: 50,
      height: 50,
      style: 'secondary',
      onClick: () => this.handleSuccessClose(),
    });
    closeButton.setDepth(Z_INDEX.tooltip);

    // Pulse checkmark
    this.tweens.add({
      targets: checkmark,
      scale: { from: 1.0, to: 1.15 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Handle success screen close
   */
  private handleSuccessClose(): void {
    // Just close the overlay and return to main menu
    this.closeScene();
  }

  /**
   * Handle install button click - browser-specific actions
   */
  private async handleInstall(): Promise<void> {
    const browserInfo = pwaManager.getBrowserInfo();

    // Firefox: Try to open in Chrome (do this BEFORE blobo jump to avoid interference)
    if (browserInfo.name === 'firefox') {
      const opened = pwaManager.tryOpenInChrome();
      if (opened) {
        // Don't jump blobo or do anything else - we're navigating away
        return;
      } else {
        // Jump blobo as feedback that button was clicked
        if (this.blobo) {
          this.blobo.jump();
        }
        return;
      }
    }

    // Make Blobo jump for all other browsers
    if (this.blobo) {
      this.blobo.jump();
    }

    // Chrome/Edge: Wait for native prompt and trigger it
    if (browserInfo.name === 'chrome' || browserInfo.name === 'edge') {
      // Check if prompt is already available
      let hasPrompt = pwaManager.hasNativePrompt();

      // If not available yet, wait for it (with 8 second timeout)
      if (!hasPrompt) {
        hasPrompt = await pwaManager.waitForInstallPrompt(8000);
      }

      if (hasPrompt) {
        try {
          const result = await pwaManager.promptNativeInstall();

          if (result === 'accepted') {
            // User accepted - show success screen immediately
            this.children.removeAll();
            this.tweens.killAll();
            this.showSuccessScreen();
          }
        } catch (error) {
          console.error('Error showing install prompt:', error);
          // On error, just acknowledge and close
          this.closeScene();
        }
      }
      // If no prompt available, manual instructions are already shown
      return;
    }

    // Desktop: Instructions are already shown (QR code)
    if (browserInfo.isDesktop) {
      return;
    }

    // Safari, Samsung, other browsers: Instructions are already shown in the scene
    // No additional action needed - the scene displays the step-by-step guide
  }

  /**
   * Close the install prompt scene (return to main menu)
   */
  private closeScene(): void {
    console.log('🌐 Closing InstallPromptScene');
    this.scene.stop();
  }
}
